import React from 'react';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';
import { Tooltip } from '@fiverr-private/tooltip';
import { Typography } from '@fiverr-private/typography';
import { SellerEntityTypes } from '@fiverr-private/upmarket_common';
import { BiData, reportEventsApi } from '../../biEvents';
import { SendTemplateModal } from '../SendTemplate';
import { UploadModal } from '../UploadModal';
import { HandlePaymentRequest, LegalDocsConfigurations } from '../../types';
import { useTemplatesData } from '../../hooks';

const TRANSLATION_PREFIX = 'legal_documents_package.seller_templates.empty_state';

export interface InitialInteractionButtonProps {
    configurations?: LegalDocsConfigurations;
    totalTemplates: number;
    activeTemplates: number;
    sellerName: string;
    sellerId: number;
    handlePayment: HandlePaymentRequest;
    triggerButton?: React.ReactNode;
    biData: BiData;
    sellerEntityType?: SellerEntityTypes;
}

const getDefaultInteractionButton = (totalTemplates: number, disabled: boolean) => {
    const translationKey = totalTemplates ? 'with_templates' : 'no_templates';

    return (
        <Button variant="ghost" size="sm" disabled={disabled} style={{ height: '32px' }}>
            <I18n k={`${TRANSLATION_PREFIX}.${translationKey}.cta`} />
        </Button>
    );
};

export const InitialInteractionButton = ({
    configurations,
    activeTemplates,
    totalTemplates,
    sellerName,
    sellerId,
    handlePayment,
    triggerButton,
    biData,
    sellerEntityType,
}: InitialInteractionButtonProps) => {
    const { sellerDataQuery } = useTemplatesData({ sellerId });
    if (!configurations) {
        return null;
    }

    if (totalTemplates) {
        return (
            <SendTemplateModal
                configurations={configurations}
                sellerName={sellerName}
                sellerId={sellerId}
                onSend={() => sellerDataQuery.refetch()}
                isDisabled={!activeTemplates}
                handlePayment={handlePayment}
                biData={biData}
                sellerEntityType={sellerEntityType}
            >
                <SendDisableTooltip shouldShow={!activeTemplates}>
                    <span
                        onClick={() => {
                            reportEventsApi.sendDocument(biData);
                        }}
                    >
                        {triggerButton || getDefaultInteractionButton(totalTemplates, !activeTemplates)}
                    </span>
                </SendDisableTooltip>
            </SendTemplateModal>
        );
    }

    return (
        <UploadModal
            biData={biData}
            isAllowedToUpload={configurations?.allowedActions?.isAllowedToUpload}
            checkEmptyTemplates
        >
            {triggerButton || getDefaultInteractionButton(totalTemplates, false)}
        </UploadModal>
    );
};

interface SendDisableTooltipProps {
    children: React.ReactNode;
    shouldShow: boolean;
}

const SendDisableTooltip = ({ children, shouldShow }: SendDisableTooltipProps) => {
    if (shouldShow) {
        return (
            <Tooltip
                size="sm"
                appendToBody
                content={
                    <Typography textAlign="center">
                        <I18n k={`${TRANSLATION_PREFIX}.disabled_send_tooltip`} />
                    </Typography>
                }
                position="bottom"
            >
                {children}
            </Tooltip>
        );
    }

    return <>{children}</>;
};
